import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import Snack from '@/common/components/molecules/Snack/Snack';
import { ENDPOINT_WISHLISTS_TREE } from '@/common/constants';
import deleteWishlistsCategory from '@/ebat/api/wishlists/categories/deleteWishlistsCategory';
import postWishlistsCategory from '@/ebat/api/wishlists/categories/postWishlistsCategory';
import putWishlistsCategory from '@/ebat/api/wishlists/categories/putWishlistsCategory';
import putWishlistsMoveCategory from '@/ebat/api/wishlists/categories/putWishlistsMoveCategory';
import postWishlist from '@/ebat/api/wishlists/postWishlist';
import putMoveWishlist from '@/ebat/api/wishlists/putMoveWishlist';

const WishlistsContext = createContext(null);

/**
 * Manage a wish lists :
 * - Add wishlist
 * - Remove wishlist
 * - update wishlist
 *
 * @param {component} children child component
 * @param {boolean} isLoggedIn If the user is properly logged in
 * @return {JSX.Element} child component with new props
 * @constructor
 */
export const WishlistsContextProvider = ({ children, isLoggedIn }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    mutate: mutatePostWishlist,
    isLoading: isLoadingPostWishlist,
  } = useMutation(postWishlist);

  /**
   * Add a wish list with or without parents
   * @param {number} categoryId If there is a category parent
   * @param {string} name Default name of wishlist
   * @param {function} onSuccess The callback triggered on success
   */
  const onAddWishlist = useCallback(
    ({ categoryId, name, onSuccess }) => {
      if (isLoggedIn) {
        mutatePostWishlist(
          {
            name,
            category_id: categoryId,
          },
          {
            onSuccess: (data) => {
              // Reset React Query cache
              queryClient.removeQueries([ENDPOINT_WISHLISTS_TREE, null]);
              onSuccess(data);
            },
            onError: (error) => {
              if (error.response.status === 500) {
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
              } else {
                toast.error(
                  <Snack message={t('common:messages:forms.errors.default')} />,
                );
              }
            },
          },
        );
      } else {
        toast.error(
          <Snack message={t('common:messages:forms.errors.not_logged')} />,
        );
      }
    },
    [isLoggedIn, mutatePostWishlist, queryClient, t],
  );

  const {
    mutate: mutatePutDragWishlist,
    isLoading: isLoadingPutWishlist,
    isSuccess: isSuccessPutWishlist,
  } = useMutation(putMoveWishlist);

  /**
   * Move wishlist with drag and drop on a new existing category
   * @param {object} item wishlist item
   * @param {object} category wishlist category
   * @param {function} onSuccess callback triggered on success
   */
  const onMoveCategory = useCallback(
    ({ item, category, onSuccess = () => {} }) => {
      mutatePutDragWishlist(
        {
          id: item.id,
          position: item.position,
          categoryId: category.id,
        },
        {
          onSuccess: (data) => {
            queryClient.setQueryData([ENDPOINT_WISHLISTS_TREE], data);
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePutDragWishlist, queryClient, t],
  );

  const {
    mutate: mutatePostCategory,
    isLoading: isLoadingPostCategory,
  } = useMutation(postWishlistsCategory);

  /**
   * Add new category or new section in the category parent
   * @param {string} name Default name of category
   * @param {number} id When there is a parent
   * @param {function} onSuccess The callback triggered on success
   */
  const onAddCategory = useCallback(
    ({ name, id, onSuccess = () => {} }) => {
      if (isLoggedIn) {
        mutatePostCategory(
          {
            name,
            parent_id: id,
          },
          {
            onSuccess: (data) => {
              queryClient.setQueryData([ENDPOINT_WISHLISTS_TREE], data);
              onSuccess(data);

              toast.success(
                <Snack message={t('common:messages:forms.success.default')} />,
              );
            },
            onError: (error) => {
              if (error.response.status === 500) {
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
              } else {
                toast.error(
                  <Snack message={t('common:messages:forms.errors.default')} />,
                );
              }
            },
          },
        );
      } else {
        toast.error(
          <Snack message={t('common:messages:forms.errors.not_logged')} />,
        );
      }
    },
    [isLoggedIn, mutatePostCategory, queryClient, t],
  );

  const {
    mutate: mutatePutCategory,
    isLoading: isLoadingPutCategory,
    isSuccess: isSuccessPutCategory,
  } = useMutation(putWishlistsCategory);

  /**
   * Update the position of the category
   * @param {object} item: category id and name
   * @param {function} onError The callback triggered on error
   * @param {function} onSuccess The callback triggered on success
   */
  const onUpdateCategory = useCallback(
    ({ item, onError = () => {}, onSuccess = () => {} }) => {
      mutatePutCategory(
        {
          id: item.id,
          name: item.name,
        },
        {
          onSuccess: (data) => {
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            onError(error);

            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePutCategory, t],
  );

  const {
    mutate: mutatePutWishlistsMoveCategory,
    isLoading: isLoadingPutWishlistsMoveCategory,
    isSuccess: isSuccessPutWishlistsMoveCategory,
  } = useMutation(putWishlistsMoveCategory);

  /**
   * Changes the positions of the sections in the categories
   * @param {object} section from categories
   * @param {function} onSuccess
   * @type {(function({section: object, onSuccess?: function}): void)|*}
   */
  const onMoveSection = useCallback(
    ({ section, onSuccess = () => {} }) => {
      mutatePutWishlistsMoveCategory(
        {
          section,
        },
        {
          onSuccess: (data) => {
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePutWishlistsMoveCategory, t],
  );

  const {
    mutate: mutateDeleteCategory,
    isLoading: isLoadingDeleteCategory,
  } = useMutation(deleteWishlistsCategory);

  /**
   * Deleted item category
   * @param {object} item category
   * @param {function} onSuccess The callback triggered on success
   */
  const onRemoveCategory = useCallback(
    ({ item, onSuccess = () => {} }) => {
      mutateDeleteCategory(item.id, {
        onSuccess: () => {
          onSuccess(); // No data sent by API on DELETE call

          toast.success(
            <Snack message={t('common:messages:forms.success.default')} />,
          );
        },
        onError: (error) => {
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      });
    },
    [mutateDeleteCategory, t],
  );

  /**
   * Generates a background colour for the drop zone
   * @param {boolean} isOver If the drop is over a dropzone
   * @param {boolean} canDrop If drop is active
   * @param {string} backgroundPrimary color background
   * @param {string} backgroundSecondary color background
   */
  const onGetBackgroundColor = useCallback(
    (isOver, canDrop, hoverColor, initialColor) => {
      if (isOver) {
        if (canDrop) {
          return hoverColor;
        }
        if (!canDrop) {
          return initialColor;
        }
      }
      return '';
    },
    [],
  );

  const isLoading = useMemo(
    () =>
      isLoadingDeleteCategory ||
      isLoadingPostCategory ||
      isLoadingPostWishlist ||
      isLoadingPutCategory ||
      isLoadingPutWishlist ||
      isLoadingPutWishlistsMoveCategory,
    [
      isLoadingDeleteCategory,
      isLoadingPostCategory,
      isLoadingPostWishlist,
      isLoadingPutCategory,
      isLoadingPutWishlist,
      isLoadingPutWishlistsMoveCategory,
    ],
  );

  const isSuccess = useMemo(
    () =>
      isSuccessPutCategory ||
      isSuccessPutWishlist ||
      isSuccessPutWishlistsMoveCategory,
    [
      isSuccessPutCategory,
      isSuccessPutWishlist,
      isSuccessPutWishlistsMoveCategory,
    ],
  );

  return (
    <WishlistsContext.Provider
      value={{
        isLoading,
        isSuccess,
        onAddCategory,
        onAddWishlist,
        onGetBackgroundColor,
        onMoveCategory,
        onMoveSection,
        onRemoveCategory,
        onUpdateCategory,
      }}
    >
      {children}
    </WishlistsContext.Provider>
  );
};

WishlistsContextProvider.defaultProps = {
  isLoggedIn: false,
};

WishlistsContextProvider.propTypes = {
  isLoggedIn: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const useWishlistsContext = () => useContext(WishlistsContext);
